import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  startSenaCouponBatch,
  getSenaCouponBatchStatus,
  getSenaCouponBatchHistory,
  checkSenaCouponDuplicates,
} from '../../api/senaApi';

const STATUS_COLORS = {
  PENDING: { bg: '#f5ece5', fg: '#a08878', label: '대기' },
  SUCCESS: { bg: '#e5f5ea', fg: '#2a8a4a', label: '성공' },
  FAILED:  { bg: '#f9e0de', fg: '#c04040', label: '실패' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.PENDING;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 8,
      fontSize: 12,
      background: s.bg,
      color: s.fg,
      fontWeight: 700,
      minWidth: 42,
      textAlign: 'center',
    }}>{s.label}</span>
  );
}

function formatDateTime(s) {
  if (!s) return '-';
  try {
    const d = new Date(s);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return s; }
}

export default function SenaCoupon() {
  const navigate = useNavigate();

  const [couponText, setCouponText] = useState('');
  const [execId, setExecId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const pollTimerRef = useRef(null);

  const parseCoupons = (text) =>
    text.split(/\r?\n|,/)
        .map(s => s.trim().replace(/﻿/g, '').toUpperCase())
        .filter(Boolean);

  const refreshHistory = useCallback(async () => {
    try {
      const r = await getSenaCouponBatchHistory();
      const list = r.data || [];
      setHistory(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  // 마운트 시 이력 로드 + 진행중 배치 있으면 자동 합류
  useEffect(() => {
    (async () => {
      const list = await refreshHistory();
      const running = list.find(h => !h.completed_at);
      if (running) setExecId(running.exec_id);
    })();
  }, [refreshHistory]);

  // execId 변경 시 폴링 시작/정지
  useEffect(() => {
    if (!execId) {
      setSummary(null);
      setDetails([]);
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const r = await getSenaCouponBatchStatus(execId);
        if (cancelled) return;
        const data = r.data;
        setSummary(data.summary);
        setDetails(data.details || []);
        if (data.summary?.completed_at) {
          // 완료 — 폴링 중지 + 이력 새로고침
          refreshHistory();
          return;
        }
        if (!cancelled) {
          pollTimerRef.current = setTimeout(poll, 3000);
        }
      } catch {
        if (!cancelled) {
          pollTimerRef.current = setTimeout(poll, 5000);
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [execId, refreshHistory]);

  const handleStart = async () => {
    const coupons = parseCoupons(couponText);
    if (coupons.length === 0) {
      setError('쿠폰 코드를 입력하세요. (줄바꿈 또는 쉼표 구분)');
      return;
    }
    setError('');

    // 성공 이력 중복 체크 (전체 이력 스캔, 표시용 10건 제한과 무관)
    // 입력 재정규화 — parseCoupons에서 이미 uppercase 하지만 방어적으로 한번 더
    const upperCoupons = coupons.map(c => c.toUpperCase());
    try {
      const r = await checkSenaCouponDuplicates(upperCoupons);
      const batches = r.data || [];
      if (batches.length > 0) {
        const lines = [];
        for (const b of batches) {
          const histCodes = (b.coupon_codes || '')
            .split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
          const hits = upperCoupons.filter(c => histCodes.includes(c));
          if (hits.length === 0) continue;
          lines.push(`  • ${hits.join(', ')} — ${formatDateTime(b.started_at)} (성공 ${b.success_count}/${b.total_count})`);
        }
        if (lines.length > 0) {
          const msg =
            `아래 쿠폰은 이미 성공한 등록 이력이 있습니다:\n\n${lines.join('\n')}\n\n` +
            `그래도 진행하시겠습니까?`;
          if (!window.confirm(msg)) return;
        }
      }
    } catch {
      // 체크 실패해도 배치 자체는 진행 가능하게 통과
    }

    setLoading(true);
    try {
      const res = await startSenaCouponBatch(coupons);
      const newExecId = res.data?.execId;
      if (newExecId) {
        setExecId(newExecId);
        setCouponText('');
      }
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 409) {
        const existingId = data?.detail?.exec_id;
        if (existingId) {
          alert('이미 진행 중인 배치가 있습니다. 기존 진행상태를 조회합니다.');
          setExecId(existingId);
          setCouponText('');
        } else {
          setError(data?.message || '이미 진행 중인 배치가 있습니다.');
        }
      } else if (status === 400) {
        setError(typeof data === 'string' ? data : (data?.message || '입력을 확인하세요.'));
      } else if (status === 403) {
        setError('관리자 권한이 필요합니다.');
      } else {
        setError('요청 실패 — 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryClick = (item) => {
    setExecId(item.exec_id);
  };

  const isRunning = summary && !summary.completed_at;
  const progressPct = summary && summary.total_count > 0
    ? Math.round(((summary.success_count + summary.fail_count) / summary.total_count) * 100)
    : 0;

  return (
    <div className="sena-page">
      <header className="sena-header">
        <button className="siege-back-btn" onClick={() => navigate('/sena')}>← 메인으로</button>
        <h1 className="sena-header-guild">쿠폰 일괄 등록</h1>
        <p className="sena-header-sub">길드원 전체에게 쿠폰 적용 (비동기)</p>
      </header>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px', fontFamily: 'Jua, sans-serif' }}>

        {/* 입력 영역 */}
        <div style={{ background: '#fff', border: '2px solid #f0d6c0', borderRadius: 14, padding: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 14, color: '#6a5040' }}>
            쿠폰 코드 (여러 개는 줄바꿈 또는 쉼표로 구분)
          </label>
          <textarea
            value={couponText}
            onChange={e => setCouponText(e.target.value)}
            placeholder="예: HAPPY2026&#10;WELCOME"
            rows={4}
            disabled={loading || isRunning}
            style={{
              width: '100%',
              padding: 12,
              border: '2px solid #f0d6c0',
              borderRadius: 10,
              fontFamily: 'Consolas, Menlo, monospace',
              fontSize: 14,
              resize: 'vertical',
              boxSizing: 'border-box',
              background: isRunning ? '#faf5f0' : '#fff',
            }}
          />

          <button
            onClick={handleStart}
            disabled={loading || isRunning}
            style={{
              width: '100%',
              marginTop: 12,
              padding: '14px 0',
              background: (loading || isRunning) ? '#c8a888' : '#e8734a',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontFamily: 'Jua, sans-serif',
              fontSize: 16,
              cursor: (loading || isRunning) ? 'default' : 'pointer',
            }}
          >
            {loading ? '요청 중...' : isRunning ? '진행 중 — 완료 후 새 요청 가능' : '길드원 전체에게 적용'}
          </button>

          {error && (
            <p style={{ color: '#c04040', fontSize: 13, marginTop: 10, marginBottom: 0 }}>{error}</p>
          )}

          <p style={{ fontSize: 12, color: '#8a6a50', margin: '12px 0 0', lineHeight: 1.5 }}>
            ※ 승인대기(PENDING) 회원은 제외됩니다.<br/>
            ※ PUUID가 등록된 회원만 대상이 됩니다.<br/>
            ※ 동시에 1개 배치만 실행 가능합니다.
          </p>
        </div>

        {/* 진행률 영역 */}
        {summary && (
          <div style={{
            marginTop: 16,
            background: '#fff',
            border: '2px solid #f0d6c0',
            borderRadius: 14,
            padding: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: '#6a5040' }}>
                {isRunning ? '🟢 진행 중' : '✅ 완료'}
              </h3>
              <span style={{ fontSize: 13, color: '#8a6a50' }}>
                시작: {formatDateTime(summary.started_at)}
                {summary.completed_at && ` · 종료: ${formatDateTime(summary.completed_at)}`}
              </span>
            </div>

            {/* 진행률 바 */}
            <div style={{ background: '#f5ece5', borderRadius: 8, height: 24, overflow: 'hidden', marginBottom: 10 }}>
              <div style={{
                background: isRunning ? '#e8a070' : '#8ab47a',
                height: '100%',
                width: `${progressPct}%`,
                transition: 'width 0.5s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
              }}>
                {progressPct}%
              </div>
            </div>

            <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#6a5040', marginBottom: 12, flexWrap: 'wrap' }}>
              <span>총 <strong>{summary.total_count}</strong>건</span>
              <span style={{ color: '#2a8a4a' }}>성공 <strong>{summary.success_count}</strong></span>
              <span style={{ color: '#c04040' }}>실패 <strong>{summary.fail_count}</strong></span>
              <span style={{ color: '#a08878' }}>대기 <strong>{summary.total_count - summary.success_count - summary.fail_count}</strong></span>
              <span>쿠폰: <code style={{ fontFamily: 'Consolas, Menlo, monospace', background: '#faf5f0', padding: '1px 6px', borderRadius: 4 }}>{summary.coupon_codes}</code></span>
            </div>

            {/* 상세 그리드 */}
            <div style={{
              border: '1px solid #f0d6c0',
              borderRadius: 10,
              overflow: 'hidden',
              fontSize: 13,
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1.2fr 70px 1.8fr',
                background: '#faf5f0',
                padding: '8px 12px',
                fontSize: 12,
                color: '#8a6a50',
                fontWeight: 700,
              }}>
                <span>닉네임</span>
                <span>쿠폰</span>
                <span style={{ textAlign: 'center' }}>상태</span>
                <span>메시지</span>
              </div>
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {details.map((d, i) => (
                  <div key={`${d.puuid}-${d.code}`} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1.2fr 70px 1.8fr',
                    padding: '8px 12px',
                    borderTop: i === 0 ? 'none' : '1px solid #f5ece5',
                    alignItems: 'center',
                    background: i % 2 === 0 ? '#fff' : '#fcfaf7',
                  }}>
                    <span style={{ color: '#4a3020' }}>{d.user_name || d.puuid.slice(0, 8)}</span>
                    <code style={{ fontFamily: 'Consolas, Menlo, monospace', color: '#6a5040', fontSize: 12 }}>{d.code}</code>
                    <span style={{ textAlign: 'center' }}><StatusBadge status={d.status} /></span>
                    <span style={{ color: '#6a5040', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={d.message || ''}>
                      {d.message || '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {!isRunning && (
              <button
                onClick={() => { setExecId(null); setSummary(null); setDetails([]); }}
                style={{
                  marginTop: 12,
                  padding: '8px 16px',
                  background: '#fff',
                  border: '2px solid #f0d6c0',
                  borderRadius: 8,
                  color: '#8a6a50',
                  cursor: 'pointer',
                  fontFamily: 'Jua, sans-serif',
                  fontSize: 13,
                }}
              >닫기</button>
            )}
          </div>
        )}

        {/* 이력 영역 */}
        <div style={{ marginTop: 16, background: '#fff', border: '2px solid #f0d6c0', borderRadius: 14, padding: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#6a5040' }}>최근 실행 이력</h3>
          {history.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#c8a888', padding: '20px 0', fontSize: 13 }}>
              이력이 없습니다.
            </p>
          ) : (
            <div style={{ border: '1px solid #f0d6c0', borderRadius: 10, overflow: 'hidden', fontSize: 13 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '140px 80px 1fr 60px 60px 60px 70px',
                background: '#faf5f0',
                padding: '8px 12px',
                fontSize: 12,
                color: '#8a6a50',
                fontWeight: 700,
                gap: 8,
              }}>
                <span>실행시각</span>
                <span>실행자</span>
                <span>쿠폰</span>
                <span style={{ textAlign: 'right' }}>총</span>
                <span style={{ textAlign: 'right' }}>성공</span>
                <span style={{ textAlign: 'right' }}>실패</span>
                <span style={{ textAlign: 'center' }}>상태</span>
              </div>
              {history.map((h, i) => {
                const done = !!h.completed_at;
                const running = !done;
                return (
                  <div key={h.exec_id}
                    onClick={() => handleHistoryClick(h)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '140px 80px 1fr 60px 60px 60px 70px',
                      padding: '8px 12px',
                      borderTop: i === 0 ? 'none' : '1px solid #f5ece5',
                      alignItems: 'center',
                      background: h.exec_id === execId ? '#fff5ec' : (i % 2 === 0 ? '#fff' : '#fcfaf7'),
                      cursor: 'pointer',
                      gap: 8,
                    }}>
                    <span style={{ color: '#4a3020', fontSize: 12 }}>{formatDateTime(h.started_at)}</span>
                    <span style={{ color: '#6a5040', fontSize: 12 }}>{h.requester_name || h.requester_id}</span>
                    <code style={{ fontFamily: 'Consolas, Menlo, monospace', color: '#6a5040', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h.coupon_codes}>{h.coupon_codes}</code>
                    <span style={{ textAlign: 'right', color: '#6a5040' }}>{h.total_count}</span>
                    <span style={{ textAlign: 'right', color: '#2a8a4a', fontWeight: 700 }}>{h.success_count}</span>
                    <span style={{ textAlign: 'right', color: '#c04040', fontWeight: 700 }}>{h.fail_count}</span>
                    <span style={{ textAlign: 'center' }}>
                      {running
                        ? <span style={{ fontSize: 11, color: '#e8734a', fontWeight: 700 }}>진행중</span>
                        : <span style={{ fontSize: 11, color: '#8a6a50' }}>완료</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <p style={{ fontSize: 11, color: '#a08878', margin: '8px 0 0' }}>
            ※ 이력 행을 클릭하면 해당 배치의 상세 진행상태를 조회합니다.
          </p>
        </div>
      </div>
    </div>
  );
}
